import React from "react";
import { Task } from "@/lib/hooks/api/tasks";
import { TableRowActionsWrapper } from "@/components/shared/UniversalCRMTable/components/SimpleTable/TableRowActionsWrapper";
import { useTaskHooks } from "@/lib/hooks/api/shared/graphql-hooks";
import { getEntityTableConfig } from "@/lib/entity-registry";
import type { EntityHooks } from "@/components/shared/UniversalCRMTable/types";
import type { Contact_Tasks } from "@/src/gql/graphql";

const TASK_CONFIG = getEntityTableConfig<Contact_Tasks>("tasks");

interface UnifiedTaskRowActionsProps {
  task: Task;
  compact?: boolean;
  refetch?: () => void | Promise<void>;
}

/**
 * UnifiedTaskRowActions - Single source of truth for task row actions
 * 
 * This component ensures that both horizontal cards and table rows
 * display the exact same actions by using the centralized TableRowActionsWrapper
 * with the task's detailActions from TASK_CONFIG.
 * 
 * Used by:
 * - HorizontalCard (for card view actions)
 * - TableRow (for table view actions)
 * - Any other component that needs task row actions
 * 
 * Benefits:
 * - Single source of truth for all task row actions
 * - Consistent behavior across all views
 * - Easy to maintain and update actions in one place
 */
export function UnifiedTaskRowActions({
  task,
  compact = true,
  refetch,
}: UnifiedTaskRowActionsProps) {
  const taskHooks = useTaskHooks();

  // Use the detail actions from TASK_CONFIG
  // These are defined in taskDetailActions and imported into TASK_CONFIG
  const detailActions = TASK_CONFIG.detailActions || [];

  return (
    <TableRowActionsWrapper
      item={task}
      actions={detailActions}
      hooks={taskHooks as unknown as EntityHooks<Task>}
      entityName="tasks"
      compact={compact}
      refetch={refetch}
    />
  );
}
